import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaders, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, from, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { StorageService } from '../services/storage.service';
import { GS1ModalService, ModalCloseReason, ModalCloseResult } from '@gs1france/gs1-components-library';

let isSessionExpiredDialogOpen = false;

export const TokenInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const oAuthService = inject(OAuthService);
  const storageService = inject(StorageService);
  const dialog = inject(GS1ModalService);
  const translate = inject(TranslateService);

  let headers: HttpHeaders = request.headers;

  const isApiRequest = request.url.includes('api');

  if (isApiRequest && oAuthService.hasValidAccessToken()) {
    const token = oAuthService.getAccessToken();
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const requestToForward = request.clone({ headers });

  return next(requestToForward).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401 && oAuthService.discoveryDocumentLoaded) {
        return from(oAuthService.tryLogin()).pipe(
          switchMap((hasToken) => {
            if (hasToken) {
              const token = oAuthService.getAccessToken();
              const retriedHeaders = request.headers.set('Authorization', `Bearer ${token}`);
              return next(request.clone({ headers: retriedHeaders }));
            }
            openSessionExpiredDialog(dialog, storageService, oAuthService, translate);
            return throwError(() => err);
          }),
          catchError(() => {
            openSessionExpiredDialog(dialog, storageService, oAuthService, translate);
            return throwError(() => err);
          })
        );
      }
      return throwError(() => err);
    })
  );
};

function openSessionExpiredDialog(dialog: GS1ModalService, storageService: StorageService, oAuthService: OAuthService, translate: TranslateService): void {
  if (isSessionExpiredDialogOpen) return;
  isSessionExpiredDialogOpen = true;

  const dialogConfig = {
    title: translate.instant('session.expired.dialogTitle'),
    body: translate.instant('session.expired.dialogText'),
    confirm: translate.instant('session.expired.dialogButtonConfirm'),
    position: {
      top: '100px'
    },
    width: '640px',
    maxWidth: '95vw',
    minHeight: '201px'
  };

  const dialogRef = dialog.open(dialogConfig);
  dialogRef
    .afterClosed()
    .pipe(
      take(1),
      tap((result: ModalCloseResult<boolean>) => {
        isSessionExpiredDialogOpen = false;
        if (result.reason === ModalCloseReason.CONFIRM) {
          storageService.clearStorage();
          oAuthService.logOut();
        }
      })
    )
    .subscribe({
      error: () => {
        isSessionExpiredDialogOpen = false;
      }
    });
}
